/**
 * Combat Audio Controller
 * Maps combat events to sound playback calls
 * @module services/sound/combat-audio-controller
 */

import type {
  CombatState,
  CombatEvent,
  CombatCreature,
  AttackStartedEventData,
  DamageDealtEventData,
} from '@drawn-of-war/shared';
import type { CreatureSoundUrls } from './types';
import { getSoundManager } from './index';
import type { SoundManager } from './sound-manager';
import {
  CombatAudioConfig,
  DEFAULT_AUDIO_CONFIG,
} from './combat-audio-types';

/**
 * Creature data needed for audio initialization
 */
export interface CreatureAudioData {
  id: string;
  sounds?: CreatureSoundUrls;
}

/**
 * Combat Audio Controller
 *
 * Processes combat state updates and triggers appropriate sounds:
 * - Attack sounds on attack_started (melee)
 * - Projectile launch sounds on attack_started (ranged)
 * - Hit sounds on damage_dealt
 * - Death sounds on unit_died
 * - Spawn sounds when new units appear
 *
 * Features:
 * - Event deduplication to prevent double-playing
 * - Unit ID to creature ID mapping
 * - Configurable sound categories
 * - Spawn detection for new units
 */
export class CombatAudioController {
  private soundManager: SoundManager;
  private config: CombatAudioConfig;

  // Event deduplication
  private processedEvents: Set<string> = new Set();
  private lastProcessedTick = -1;

  // Unit mapping (combat unitId -> creature document ID)
  private unitToCreatureId: Map<string, string> = new Map();

  // Spawn detection
  private knownUnitIds: Set<string> = new Set();

  constructor(config?: Partial<CombatAudioConfig>) {
    this.soundManager = getSoundManager();
    this.config = { ...DEFAULT_AUDIO_CONFIG, ...config };
  }

  /**
   * Initialize with creature data for the combat session
   * Preloads all creature sounds and shared sounds
   * @param creatures - Array of creature documents with sound URLs
   */
  async initialize(creatures: CreatureAudioData[]): Promise<void> {
    console.log(`[CombatAudio] Initializing with ${creatures.length} creatures`);

    // Preload shared sounds first
    await this.soundManager.preloadSharedSounds();

    // Preload each creature's sounds
    const preloadPromises = creatures.map(async (creature) => {
      if (creature.sounds) {
        await this.soundManager.preloadCreatureSounds(creature.id, creature.sounds);
      }
    });

    await Promise.allSettled(preloadPromises);

    console.log('[CombatAudio] Initialization complete');
  }

  /**
   * Set mapping from combat unit IDs to creature document IDs
   * Call this when combat state first arrives or when units spawn
   * @param unitIdToCreatureId - Map of unitId -> creatureId
   */
  setUnitMapping(unitIdToCreatureId: Map<string, string>): void {
    this.unitToCreatureId = unitIdToCreatureId;
  }

  /**
   * Build unit mapping from combat state
   * Extracts unitId -> creatureId from all units
   * @param state - Combat state with units array
   */
  buildUnitMappingFromState(state: CombatState): void {
    for (const unit of state.units) {
      if (!this.unitToCreatureId.has(unit.unitId)) {
        this.unitToCreatureId.set(unit.unitId, unit.creatureId);
      }
    }
  }

  /**
   * Process combat state update and trigger sounds
   * Call this on every state update from CombatVisualizationManager
   * @param state - Current combat state
   */
  processStateUpdate(state: CombatState): void {
    if (!this.config.enabled) return;

    // Build/update unit mapping
    this.buildUnitMappingFromState(state);

    // Detect new units (spawns)
    this.detectSpawns(state);

    // Process events
    if (state.events && state.events.length > 0) {
      this.processEvents(state.events);
    }
  }

  /**
   * Detect newly spawned units and play spawn sounds
   */
  private detectSpawns(state: CombatState): void {
    if (!this.config.playSpawnSounds) return;

    for (const unit of state.units) {
      if (!this.knownUnitIds.has(unit.unitId)) {
        this.knownUnitIds.add(unit.unitId);
        // Only play spawn sound if unit is alive (not for dead units in initial state)
        if (unit.status === 'alive') {
          this.soundManager.playSharedSound('spawn');
        }
      }
    }
  }

  /**
   * Process combat events and trigger sounds
   */
  private processEvents(events: CombatEvent[]): void {
    // Filter to only new events
    const newEvents = events.filter((event) => event.tick > this.lastProcessedTick);

    for (const event of newEvents) {
      const eventId = this.generateEventId(event);

      // Skip if already processed (deduplication)
      if (this.processedEvents.has(eventId)) continue;
      this.processedEvents.add(eventId);

      this.processEvent(event);
    }

    // Update last processed tick
    if (newEvents.length > 0) {
      this.lastProcessedTick = Math.max(...newEvents.map((e) => e.tick));
    }

    // Clean up old event IDs (keep last 200, delete oldest 100)
    if (this.processedEvents.size > 200) {
      const toDelete = Array.from(this.processedEvents).slice(0, 100);
      toDelete.forEach((id) => this.processedEvents.delete(id));
    }
  }

  /**
   * Generate unique ID for event deduplication
   */
  private generateEventId(event: CombatEvent): string {
    return `${event.tick}-${event.eventType}-${JSON.stringify(event.data)}`;
  }

  /**
   * Process a single combat event
   */
  private processEvent(event: CombatEvent): void {
    switch (event.eventType) {
      case 'attack_started':
        this.handleAttackStarted(event.data as AttackStartedEventData);
        break;
      case 'damage_dealt':
        this.handleDamageDealt(event.data as DamageDealtEventData);
        break;
      case 'unit_died':
        this.handleUnitDied(event.data);
        break;
      case 'projectile_spawned':
        // Projectile launch is handled in attack_started for ranged attacks
        // Could add travel/whoosh sounds here if needed
        break;
    }
  }

  /**
   * Handle attack_started event
   * Plays attack sound for melee, projectileLaunch for ranged
   */
  private handleAttackStarted(data: AttackStartedEventData): void {
    if (!this.config.playAttackSounds) return;

    const attackerId = data.attackerId;
    const creatureId = this.unitToCreatureId.get(attackerId);

    if (!creatureId) {
      console.warn(`[CombatAudio] No creature ID for attacker ${attackerId}`);
      return;
    }

    const attackType = data.attackType;

    if (attackType === 'ranged' || attackType === 'ability') {
      // Ranged attacks trigger projectileLaunch sound
      if (this.config.playProjectileSounds) {
        this.soundManager.playSound(creatureId, 'projectileLaunch');
      }
    } else {
      // Melee attacks trigger attack sound
      this.soundManager.playSound(creatureId, 'attack');
    }
  }

  /**
   * Handle damage_dealt event
   * Plays hit sound on the victim
   */
  private handleDamageDealt(data: DamageDealtEventData): void {
    if (!this.config.playHitSounds) return;

    const victimId = data.victimId;
    const creatureId = this.unitToCreatureId.get(victimId);

    if (!creatureId) {
      console.warn(`[CombatAudio] No creature ID for victim ${victimId}`);
      return;
    }

    this.soundManager.playSound(creatureId, 'hit');
  }

  /**
   * Handle unit_died event
   * Plays shared death sound
   */
  private handleUnitDied(data: Record<string, unknown>): void {
    if (!this.config.playDeathSounds) return;

    // Death sound is shared, not creature-specific
    this.soundManager.playSharedSound('death');
  }

  /**
   * Update configuration at runtime
   * @param config - Partial config to merge
   */
  setConfig(config: Partial<CombatAudioConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Enable or disable all audio
   * @param enabled - Whether audio is enabled
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Check if audio is enabled
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Get current configuration
   */
  getConfig(): CombatAudioConfig {
    return { ...this.config };
  }

  /**
   * Clean up after combat ends
   * Resets all state for next combat session
   */
  cleanup(): void {
    this.processedEvents.clear();
    this.unitToCreatureId.clear();
    this.knownUnitIds.clear();
    this.lastProcessedTick = -1;
    console.log('[CombatAudio] Cleanup complete');
  }
}
